/**
 * Warranty lifecycle states derived from time elapsed since the warranty's
 * start date, scaled proportionally to the warranty's `period_months` so
 * the same milestone framework applies to 12, 24 or 36 month warranties.
 *
 * The canonical timeline (from the customer's reference document) is
 * pinned to a 24-month warranty:
 *   Day 0           Start of Warranty
 *   18 months  (75.0%)  Pre-Inspection Trigger
 *   21 months  (87.5%)  Formal Warranty Inspection
 *   21–22      (~91.7%) Contractor Notification
 *   22–23      (~95.8%) Repair Completion
 *   23–24      (~100%)  Final Verification
 *   24 months  (100%)   Warranty Expiration
 */

export interface WarrantyState {
  key: string;
  /** Milestone name (kept for backwards-compatibility, not user-shown). */
  milestone: string;
  /** Purpose-of-milestone copy from the customer's reference doc;
   * used as the column label and the step label on the diagram. */
  label: string;
  /** Fraction of warranty period at which this state begins (0–1). */
  startFraction: number;
}

export const WARRANTY_STATES: WarrantyState[] = [
  { key: 'start',                   milestone: 'Start of Warranty',          label: 'Official warranty begins',                  startFraction: 0     },
  { key: 'pre_inspection_trigger',  milestone: 'Pre-Inspection Trigger',     label: 'Prepare for formal inspection',             startFraction: 0.75  },
  { key: 'formal_inspection',       milestone: 'Formal Warranty Inspection', label: 'Identify all deficiencies before expiration', startFraction: 0.875 },
  { key: 'contractor_notification', milestone: 'Contractor Notification',    label: 'Issue formal repair notice',                startFraction: 0.917 },
  { key: 'repair_completion',       milestone: 'Repair Completion',          label: 'Contractor performs corrective work',       startFraction: 0.958 },
  { key: 'final_verification',      milestone: 'Final Verification',         label: 'Confirm repairs',                           startFraction: 0.99  },
  { key: 'expiration',              milestone: 'Warranty Expiration',        label: 'Closeout',                                  startFraction: 1     },
];

export interface Warranty {
  start_date?: string;
  end_date?: string;
  period_months?: number;
  status?: string;
}

/**
 * Pick the current state for a warranty.
 *  - returns `expiration` once end_date has passed (or status='expired').
 *  - if start_date isn't set, returns null.
 */
export function computeWarrantyState(warranty: Warranty | null | undefined): WarrantyState | null {
  if (!warranty?.start_date) return null;
  const start = new Date(warranty.start_date).getTime();
  if (Number.isNaN(start)) return null;

  const now = Date.now();
  const months = Number(warranty.period_months) || 24;
  const end = warranty.end_date
    ? new Date(warranty.end_date).getTime()
    : start + months * 30.4375 * 86400_000;
  const total = end - start;
  if (total <= 0) return WARRANTY_STATES[WARRANTY_STATES.length - 1];

  if (warranty.status === 'expired' || now >= end) {
    return WARRANTY_STATES[WARRANTY_STATES.length - 1];
  }

  const fraction = (now - start) / total;
  // Walk from the last "true milestone" backwards — the first one whose
  // startFraction is <= fraction wins.
  for (let i = WARRANTY_STATES.length - 1; i >= 0; i -= 1) {
    if (fraction >= WARRANTY_STATES[i].startFraction) {
      return WARRANTY_STATES[i];
    }
  }
  return WARRANTY_STATES[0];
}
